import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API, getWishlist } from "./store";

const AuthContext = createContext({ user: null, loading: true, setUser: () => {}, logout: () => {} });
export const useAuth = () => useContext(AuthContext);

export function login() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Session hint avoids pointless guest /auth/me 401s on every page load
  const applyUser = useCallback((u) => {
    if (u) localStorage.setItem("rj_session_hint", "1");
    else localStorage.removeItem("rj_session_hint");
    setUser(u);
  }, []);

  const checkAuth = useCallback(async () => {
    if (!localStorage.getItem("rj_session_hint")) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      applyUser(res.data);
    } catch (e) {
      applyUser(null);
    } finally {
      setLoading(false);
    }
  }, [applyUser]);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (!user) return;
    const push = () =>
      axios
        .put(`${API}/user/wishlist`, { handles: getWishlist() }, { withCredentials: true })
        .catch((e) => console.warn("Wishlist sync failed:", e?.message));
    window.addEventListener("rj-wishlist", push);
    return () => window.removeEventListener("rj-wishlist", push);
  }, [user]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      console.warn("Logout request failed:", e?.message);
    }
    setUser(null);
    localStorage.removeItem("rj_session_hint");
  };

  return (
    <AuthContext.Provider value={{ user, setUser: applyUser, loading, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
